import React from 'react'
import 'bootstrap/dist/css/bootstrap.min.css';
import { useDarkMode } from '../utils/DarkModeProvider';
import { useNavigate } from "react-router-dom"
import { useAuth } from '../pages/AuthContext';
import Swal from 'sweetalert2';

export default function ListadoED({ usuario, evaluaciones, textosIngreso = [] }) {

    const { darkMode } = useDarkMode();
    const navigate = useNavigate()
    const { token } = useAuth()

    const dynamicStyles = darkMode ? darkModeStyles : lightModeStyles;
    const ingresarButtonTheme = darkMode ? styles.ingresarButtonDark : styles.ingresarButtonLight
    const ingresarButtonHoverTheme = darkMode ? styles.ingresarButtonHoverDark : styles.ingresarButtonHoverLight

    const formatearFecha = (fecha) => {
        if (!fecha || typeof fecha !== 'string' || !fecha.includes('-')) return '-'
        const [year, month, day] = fecha.split('-')
        return `${day}/${month}/${year}`
    }

    const escapeHtml = (value) => String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')

    const renderTextoResaltadoHtml = (value) => {
        const partes = String(value || '').split(/(\b\d{1,2}\/\d{1,2}\/\d{4}\b|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g)

        return partes.map((parte) => {
            if (!parte) return ''

            if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(parte)) {
                return `<strong>${escapeHtml(parte)}</strong>`
            }

            if (/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(parte)) {
                return `<span style="color:#38bdf8; font-weight:600;">${escapeHtml(parte)}</span>`
            }

            return escapeHtml(parte)
        }).join('')
    }

    const construirHtmlTextosIngresoAcceso = () => {
        const textoIntroPorDefecto = 'Tendrá acceso al formulario en las siguientes fechas, según la unidad académica de pertenencia. Si el período de carga hubiera caducado o tiene alguna consulta, deberá comunicarse por mail.'
        const textoIntro = (textosIngreso || []).find((item) => Number(item?.id) === 1 || Number(item?.orden) === 0)?.texto || textoIntroPorDefecto

        const idsPreferidos = new Set([2, 3, 4, 5])
        const porIds = (textosIngreso || []).filter((item) => idsPreferidos.has(Number(item?.id)))
        const seleccion = porIds.length > 0
            ? porIds
            : (textosIngreso || []).filter((item) => {
                const orden = Number(item?.orden)
                return orden >= 1 && orden <= 4
            })

        if (seleccion.length === 0) {
            return `<div style="margin-bottom:10px; white-space:pre-line; text-align:left;">${renderTextoResaltadoHtml(textoIntro)}</div>`
        }

        const detalleUnidades = seleccion
            .sort((a, b) => Number(a?.orden ?? 0) - Number(b?.orden ?? 0))
            .map((item) => `<div style="margin-bottom:10px; white-space:pre-line; text-align:left;">${renderTextoResaltadoHtml(item?.texto)}</div>`)
            .join('')

        return `<div style="margin-bottom:12px; white-space:pre-line; text-align:left;">${renderTextoResaltadoHtml(textoIntro)}</div>${detalleUnidades}`
    }

    const irAFormulario = async (evaluacion) => {
        if (!usuario?.id || !evaluacion?.id || !token) {
            navigate('/formulario', {
                replace: true,
                state: {
                    usuario,
                    evaluacion
                }
            })
            return
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/docente-portada/${usuario.id}/${evaluacion.id}/`, {
                method: 'GET',
                headers: {
                    accept: 'application/json',
                    Authorization: `Bearer ${token}`
                }
            })

            if (!response.ok) {
                let backendDetail = 'No se pudo validar el estado del docente'
                try {
                    const errorData = await response.json()
                    backendDetail = String(errorData?.detail || errorData?.message || backendDetail).trim()
                } catch {
                    try {
                        const errorText = await response.text()
                        backendDetail = String(errorText || backendDetail).trim()
                    } catch {
                        backendDetail = 'No se pudo validar el estado del docente'
                    }
                }

                if (backendDetail === 'El docente no tiene acceso al periodo seleccionado') {
                    await Swal.fire({
                        icon: 'info',
                        title: 'Acceso por unidad académica',
                        html: construirHtmlTextosIngresoAcceso(),
                        confirmButtonText: 'Cerrar',
                        confirmButtonColor: '#972034'
                    })
                    return
                }

                await Swal.fire({
                    icon: 'warning',
                    title: 'Acceso bloqueado',
                    text: backendDetail,
                    confirmButtonText: 'Cerrar',
                    confirmButtonColor: '#972034'
                })
                return
            }

            const portadaDocente = await response.json()
            const estadoActual = String(portadaDocente?.estado_actual || '').trim().toLowerCase()

            if (estadoActual === 'carga_cerrada') {
                navigate('/resumen', {
                    replace: true,
                    state: {
                        usuario,
                        evaluacion,
                        bloquearEdicion: true
                    }
                })
                return
            }
        } catch (errorEstado) {
            console.log(errorEstado)
            Swal.fire({
                icon: 'warning',
                title: 'Advertencia',
                text: 'No se pudo validar el estado del docente. Se abrirá el formulario.',
                confirmButtonText: 'Cerrar',
                confirmButtonColor: '#972034'
            })
        }

        navigate('/formulario', {
            replace: true,
            state: {
                usuario,
                evaluacion
            }
        })
    }

    return (
        <div className="mb-2">
            <>
                <h2 className="pb-2" style={{ ...dynamicStyles.title, fontSize: '1.45rem' }}>Seleccione una evaluación</h2>
                {Array.isArray(evaluaciones) && evaluaciones.length > 0 ? (
                    <div className="list-group">
                        {evaluaciones.map((evaluacion) => {
                            const fueEvaluado = evaluacion?.fecha_fin
                                ? new Date(evaluacion.fecha_fin) < new Date()
                                : false;

                            return (
                                <div
                                    key={evaluacion.id}
                                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center shadow-sm rounded mb-2 ${fueEvaluado ? 'border-success' : ''}`}
                                    style={{
                                        cursor: "pointer",
                                        borderLeft: fueEvaluado ? "6px solid #4CAF50" : "6px solid transparent",
                                        backgroundColor: darkMode && fueEvaluado ? "rgba(76, 175, 80, 0.15)" : !darkMode && fueEvaluado ? "#e8f5e9" : undefined,
                                        borderColor: fueEvaluado ? "#4CAF50" : "transparent",
                                        color: darkMode && fueEvaluado ? "#e8f5e9" : undefined,
                                    }}
                                    onClick={() => irAFormulario(evaluacion)}
                                >
                                    <div>
                                        <h5 className="mb-1">
                                            Período de Evaluación {formatearFecha(evaluacion.fecha_inicio)} - {formatearFecha(evaluacion.fecha_fin)}
                                        </h5>
                                        <small>
                                            {evaluacion.descripcion ? evaluacion.descripcion : 'Sin descripción'}
                                        </small>
                                    </div>
                                    <button
                                        type="button"
                                        style={{ ...styles.ingresarButtonBase, ...ingresarButtonTheme }}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            irAFormulario(evaluacion)
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.backgroundColor = ingresarButtonHoverTheme.backgroundColor
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.backgroundColor = ingresarButtonTheme.backgroundColor
                                        }}
                                    >
                                        Ingresar
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p style={{ marginTop: 12, fontStyle: 'italic' }}>No hay evaluaciones disponibles.</p>
                )}
            </>
        </div>
    )
}

const lightModeStyles = {
    title: {
        color: '#A23C41',
        borderBottom: '4px solid #646354'
    }
};

const darkModeStyles = {
    title: {
        borderBottom: '4px solid #495057'
    }
};

const styles = {
    ingresarButtonBase: {
        border: 'none',
        borderRadius: 6,
        padding: '7px 12px',
        fontSize: 14,
        fontWeight: 700,
        color: '#fff',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        minWidth: 90,
        marginLeft: 12,
        flexShrink: 0
    },
    ingresarButtonLight: {
        backgroundColor: '#972034'
    },
    ingresarButtonHoverLight: {
        backgroundColor: '#7f1b2d'
    },
    ingresarButtonDark: {
        backgroundColor: '#b53a4f'
    },
    ingresarButtonHoverDark: {
        backgroundColor: '#972034'
    }
};